import { useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

import ROUTES from 'config/routes';
import Image from 'next/image';
import NextLink from 'shared/components/NextLink';
import { ButtonWrapper } from 'shared/components/Styled';
import useTranslation from 'shared/hooks/useTranslation';

import BlurElement from './BlurElement';
import { DesktopBlur, MobileBlur, Wrapper } from './styled';
import { parallaxOnScroll } from './utils';

function TeamTrustMate(): JSX.Element {
  const { t } = useTranslation('ourTeamPage');
  const allImagesRef = useRef<HTMLDivElement>(null);
  const robertRef = useRef<HTMLDivElement>(null);
  const pawelRef = useRef<HTMLDivElement>(null);
  const jerzyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleParallax() {
      if (robertRef.current && pawelRef.current && jerzyRef.current && allImagesRef.current) {
        window.addEventListener('scroll', () => {
          if (robertRef.current) {
            parallaxOnScroll({ element: robertRef.current, speed: 0.05 });
          }
          if (jerzyRef.current) {
            parallaxOnScroll({ element: jerzyRef.current, speed: 0.1 });
          }
          if (pawelRef.current) {
            parallaxOnScroll({ element: pawelRef.current, speed: 0.05 });
          }
          if (allImagesRef.current) {
            parallaxOnScroll({ element: allImagesRef.current, speed: 0.23, direction: 'down' });
          }
        });
      }
    }

    handleParallax();
    return () => {
      handleParallax();
    };
  }, []);

  return (
    <>
      <StyledWrapper>
        <DesktopBlur>
          <BlurElement speed={0.2} size={500} position={{ x: 50, y: -200 }} />
          <BlurElement speed={0.2} direction="right" size={600} position={{ x: 50, y: -600 }} />
        </DesktopBlur>
        <MobileBlur>
          <BlurElement speed={0.2} size={300} position={{ x: 50, y: -150 }} />
        </MobileBlur>
        <HeaderImagesWrapper>
          <HeaderWrapper>
            <HeaderH1>Team TrustMate!</HeaderH1>
            <HeaderH2>{t('meetOurExperts')}</HeaderH2>
          </HeaderWrapper>
          <Images ref={allImagesRef} id="all-images">
            <ImageWrapper>
              <Image src="/img/our-team/team/lukasz-tm.png" alt="" height={534} width={355} quality={100} />
            </ImageWrapper>
            <ImageWrapper>
              <Image src="/img/our-team/team/ania-tm.png" alt="" height={500} width={384} quality={100} />
            </ImageWrapper>
            <ImageWrapper>
              <Image src="/img/our-team/team/martyna-tm.png" alt="" height={479} width={357} quality={100} />
            </ImageWrapper>
            <ImageWrapper>
              <Image src="/img/our-team/team/piotr-tm.png" alt="" height={510} width={349} quality={100} />
            </ImageWrapper>
            <ImageWrapper>
              <Image src="/img/our-team/team/maja-tm.png" alt="" height={449} width={317} quality={100} />
            </ImageWrapper>
            <ImageWrapper ref={pawelRef}>
              <Image src="/img/our-team/team/pawel-tm.png" alt="" height={526} width={371} quality={100} />
            </ImageWrapper>
            <ImageWrapper ref={jerzyRef}>
              <Image src="/img/our-team/team/jurek-tm.png" alt="" height={553} width={379} quality={100} />
            </ImageWrapper>
            <ImageWrapper ref={robertRef}>
              <Image src="/img/our-team/team/robert-tm.png" alt="" height={519} width={358} quality={100} />
            </ImageWrapper>
            <ImageWrapper id="julia">
              <Image src="/img/our-team/team/julia-tm.png" alt="" height={419} width={298} quality={100} />
            </ImageWrapper>
            <ImageWrapper id="damian">
              <Image src="/img/our-team/team/damian-tm.png" alt="" height={399} width={266} quality={100} />
            </ImageWrapper>
            <ImageWrapper id="natalia">
              <Image src="/img/our-team/team/natalia-tm.png" alt="" height={483} width={364} quality={100} />
            </ImageWrapper>
            <ImageWrapper id="dawid">
              <Image src="/img/our-team/team/dawid-tm.png" alt="" height={400} width={275} quality={100} />
            </ImageWrapper>
            <ImageWrapper id="maks">
              <Image src="/img/our-team/team/maks-tm.png" alt="" height={507} width={356} quality={100} />
            </ImageWrapper>
            <ShadowWrapper>
              <Shadow />
            </ShadowWrapper>
          </Images>
        </HeaderImagesWrapper>
      </StyledWrapper>
      <BottomWrapper>
        <StyledButtonWrapper columns="2" justifyContent="center">
          <NextLink href={ROUTES.PRICING}>
            <LeftAnimatedButton>{t('chooseTrustMate')} 🔥</LeftAnimatedButton>
          </NextLink>
          <NextLink href={ROUTES.JOBS_BOARD}>
            <RightAnimatedButton>{t('joinOurTeam')}</RightAnimatedButton>
          </NextLink>
        </StyledButtonWrapper>
        <YourImage>{t('yourImageInGoodHands')} 🏆</YourImage>
      </BottomWrapper>
    </>
  );
}

const StyledWrapper = styled(Wrapper)`
  z-index: -1;
  max-width: 100vw;
  position: relative;
  margin-left: 0;
  margin-right: 0;

  @media ${({ theme }) => theme.DEVICE.LG} {
    margin-left: -3.2rem;
    margin-right: -3.2rem;
  }
`;

const HeaderImagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  z-index: -1;
  max-width: 100vw;
  overflow: hidden;
`;

const Header = css`
  font-weight: 500;
  letter-spacing: 0.75px;
  text-align: center;
  color: #fff;
`;

const HeaderH1 = styled.h1`
  ${Header}
  font-size: 2.8rem;
  line-height: 2.99;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 7.2rem;
    line-height: 1.16;
  }
`;

const HeaderH2 = styled.h2`
  ${Header}
  font-size: 2.4rem;
  line-height: 1.25;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 5.4rem;
    line-height: 1.55;
  }
`;

const BottomWrapper = styled.div`
  transform: translateY(-20rem);

  @media ${({ theme }) => theme.DEVICE.MD} {
    transform: translateY(-12rem);
  }
`;

const StyledButtonWrapper = styled(ButtonWrapper)`
  @media (max-width: ${({ theme }) => theme.BREAKPOINT.MD}) {
    grid-template-columns: 1fr;
  }
`;

const Animation = css`
  all: unset;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  padding: 1.6rem 3.6rem;
  font-size: 1.9rem;
  cursor: pointer;
  color: white;
  text-transform: uppercase;
  width: fit-content;
`;

const RightAnimatedButton = styled.button`
  ${Animation};
  background: linear-gradient(
    270deg,
    rgba(16, 16, 16, 0.75) 51%,
    rgba(252, 121, 0, 0.9) 51%,
    rgba(0, 0, 0, 0.9) 99.64%
  );
  background-size: 205%;
  transition: background-position 0.3s ease-in-out;
  background-position: right center;

  :hover {
    background-position: left center;
  }
`;

const LeftAnimatedButton = styled.button`
  ${Animation};
  background: linear-gradient(90deg, rgba(16, 16, 16, 0.75) 51%, rgba(252, 121, 0, 0.9) 51%, rgba(0, 0, 0, 0.9) 99.64%);
  background-size: 205%;
  transition: background-position 0.3s ease-in-out;

  :hover {
    background-position: right center;
  }
`;

const YourImage = styled.div`{
  font-size: 1.8rem;
  letter-spacing: 0.75px;
  text-align: center;
  margin-top: 0.8rem;
  color: ${({ theme }) => theme.COLORS.V2.SECONDARY};
}`;

const HeaderWrapper = styled.header`
  transform: translateY(2rem);
  z-index: 0;

  @media ${({ theme }) => theme.DEVICE.MD} {
    transform: translateY(5rem);
  }
`;

const fadeIn = keyframes`
from {
  opacity: 0;
}
to {
  opacity: 1;
}
`;

const Images = styled.div`
  z-index: 0;
  position: relative;
  height: 56rem;
  width: 0.2rem;
  margin: 0 auto;
  animation-delay: 1s;

  > div:nth-of-type(1) {
    right: 46rem;
    height: 355px;
    width: 534px;
    animation: ${fadeIn} 2s ease-in-out;
  }

  > div:nth-of-type(2) {
    right: 47rem;
    height: 500px;
    width: 384px;
    animation: ${fadeIn} 2s ease-in-out;
  }

  > div:nth-of-type(3) {
    right: 34rem;
    height: 479px;
    width: 357px;
    animation: ${fadeIn} 2s ease-in-out;
  }

  > div:nth-of-type(4) {
    z-index: -1;
    right: 19rem;
    height: 510px;
    width: 349px;
    animation: ${fadeIn} 2s ease-in-out;
  }

  > div:nth-of-type(5) {
    top: 1rem;
    right: 10rem;
    height: 449px;
    width: 317px;
    animation: ${fadeIn} 2s ease-in-out;
  }

  > div:nth-of-type(6) {
    z-index: -1;
    top: -2rem;
    right: -4rem;
    height: 526px;
    width: 371px;
    animation: ${fadeIn} 1s ease-in-out;
  }

  > div:nth-of-type(7) {
    top: -5rem;
    right: -20rem;
    height: 553px;
    width: 379px;
    animation: ${fadeIn} 1s ease-in-out;
    animation-delay: 0s;
  }

  > div:nth-of-type(8) {
    z-index: -1;
    left: -2rem;
    height: 519px;
    width: 358px;
    animation: ${fadeIn} 1s ease-in-out;
    animation-delay: 0s;
  }

  > div:nth-of-type(9) {
    top: 6rem;
    left: 15rem;
    height: 419px;
    width: 298px;
    animation: ${fadeIn} 2s ease-in-out;
  }

  > div:nth-of-type(10) {
    z-index: -1;
    left: 30rem;
    height: 399px;
    width: 266px;
    animation: ${fadeIn} 2s ease-in-out;
  }

  > div:nth-of-type(11) {
    left: 35rem;
    height: 483px;
    width: 364px;
    animation: ${fadeIn} 2s ease-in-out;
  }

  > div:nth-of-type(12) {
    z-index: -1;
    left: 55rem;
    height: 400px;
    width: 275px;
    animation: ${fadeIn} 2s ease-in-out;
  }

  > div:nth-of-type(13) {
    left: 65rem;
    height: 507px;
    width: 356px;
    animation: ${fadeIn} 2s ease-in-out;
  }
`;

const ShadowWrapper = styled.div`
  position: absolute;
  top: 15rem;
  width: 100vw;
  height: 60rem;
`;

const Shadow = styled.div`
  z-index: -1;
  background: linear-gradient(
    360deg,
    rgba(0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 64%,
    rgba(0, 37, 44, 0) 85%,
    rgba(0, 212, 255, 0) 100%
  );
  height: 100%;
  width: 100%;
  transform: translateX(-50%);
`;

const ImageWrapper = styled.div`
  position: absolute;
`;

export default TeamTrustMate;
